// Funzioni pure a supporto delle colonne configurabili (`configurableColumn.jsx`):
// lettura del valore dalla riga, formattazione per tipo, valutazione delle
// regole condizionali, traduzione dello stile di config in CSS inline.

import dayjs from 'dayjs';

import {
  DEFAULT_COLUMN_STYLE,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATETIME_FORMAT,
  RULE_OPS_WITHOUT_VALUE,
} from '@/constants/dashboardColumns';

/** Legge `a.b.c` dalla riga; ritorna undefined se un livello manca. */
export function getValueByPath(row, path) {
  if (!row || !path) return undefined;
  if (!path.includes('.')) return row[path];
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), row);
}

/**
 * Normalizza in dayjs i formati data che girano nell'app: Timestamp Firestore
 * (`seconds` da SDK client, `_seconds` da REST), Date, numero (ms), stringa.
 */
export function toDayjs(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === 'number') return dayjs(seconds * 1000);
    if (typeof value.toDate === 'function') return dayjs(value.toDate());
  }
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

/** Sostituisce i `{campo}` del template con i valori della riga (vuoto se assente). */
export function applyTemplate(template, row) {
  if (!template) return '';
  return template.replace(/\{([^{}]+)\}/g, (_, path) => {
    const v = getValueByPath(row, path.trim());
    return v == null ? '' : String(v);
  });
}

/**
 * Valore testuale di una colonna custom, per i tipi che rendono testo semplice.
 * I tipi semantici (currency/passenger/route/preset) sono gestiti dal renderer.
 */
export function formatValue(value, cfg = {}) {
  switch (cfg.type) {
    case 'number': {
      if (value == null || value === '') return '';
      const n = Number(value);
      return Number.isNaN(n) ? String(value) : n.toLocaleString('it-IT');
    }
    case 'date': {
      const d = toDayjs(value);
      return d ? d.format(cfg.format || DEFAULT_DATE_FORMAT) : '';
    }
    case 'datetime': {
      const d = toDayjs(value);
      return d ? d.format(cfg.format || DEFAULT_DATETIME_FORMAT) : '';
    }
    case 'boolean':
      return value ? (cfg.trueLabel ?? 'SI') : (cfg.falseLabel ?? 'NO');
    default:
      return value == null ? '' : String(value);
  }
}

/** Valuta una singola regola sul valore grezzo della cella. */
export function matchRule(rule, value) {
  if (!rule?.op) return false;
  const isEmpty = value == null || value === '' || value === false;
  switch (rule.op) {
    case 'empty':
      return isEmpty;
    case 'not_empty':
      return !isEmpty;
    case 'eq':
      return String(value ?? '').toLowerCase() === String(rule.value ?? '').toLowerCase();
    case 'neq':
      return String(value ?? '').toLowerCase() !== String(rule.value ?? '').toLowerCase();
    case 'contains':
      return String(value ?? '')
        .toLowerCase()
        .includes(String(rule.value ?? '').toLowerCase());
    case 'gt':
      return Number(value) > Number(rule.value);
    case 'lt':
      return Number(value) < Number(rule.value);
    default:
      return false;
  }
}

/**
 * Stile effettivo della cella: base + stile della PRIMA regola che matcha
 * (le regole successive non si accumulano — comportamento prevedibile).
 */
export function resolveStyle(style, rules, value) {
  const base = { ...DEFAULT_COLUMN_STYLE, ...(style || {}) };
  const hit = (rules || []).find((r) => matchRule(r, value));
  return hit?.style ? { ...base, ...hit.style } : base;
}

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

/**
 * Config di stile → style inline, separati in due livelli: `wrapper` (riempie
 * la cella: allineamento, sfondo) e `inner` (il testo: colore, peso,
 * troncamento). Il troncamento sta sull'inner perché su un flex container
 * `text-overflow` non ha effetto.
 */
export function styleToCss(style) {
  const s = { ...DEFAULT_COLUMN_STYLE, ...(style || {}) };
  const wrapper = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: JUSTIFY[s.align] || 'flex-start',
    width: '100%',
    height: '100%',
    minWidth: 0,
  };
  const inner = { minWidth: 0, textAlign: s.align };
  if (s.weight === 'bold') inner.fontWeight = 600;
  if (s.color) inner.color = `var(${s.color})`;
  if (s.background) {
    wrapper.background = `var(${s.background})`;
    wrapper.padding = '0 var(--space-sm)';
  }
  if (s.ellipsis) {
    inner.overflow = 'hidden';
    inner.textOverflow = 'ellipsis';
    inner.whiteSpace = 'nowrap';
  }
  return { wrapper, inner };
}

/** Una regola è salvabile solo se completa (op + valore quando richiesto). */
export function isRuleComplete(rule) {
  if (!rule?.op) return false;
  if (RULE_OPS_WITHOUT_VALUE.includes(rule.op)) return true;
  return rule.value != null && rule.value !== '';
}
